/**
 * AskHub.utils.ts
 *
 * Utility functions for formatting and displaying ask session data in AskHub.
 * Handles date formatting and user display metadata (name, image, avatar data)
 * from workspace data.
 */

// ============= Imports =============

import { DEFAULT_AVATAR_FALLBACK } from '@/apis/core/user/get/getUserImage';
import { getUserDisplayNameFromRecord } from '@/utils/userDisplayName';

// Re-export so consumers can use one import for workspace lookups and fallback.
export { DEFAULT_AVATAR_FALLBACK };

// ============= Types =============

/** Workspace data shape for user display name lookup. */
interface WorkspaceDataForDisplayName {
  workspaceUsers?: {
    userMetadata?: Record<string, { userId?: string }>;
    userData?: Record<string, { firstName?: string; lastName?: string }>;
  };
}

/** Workspace data shape for user image lookup. */
interface WorkspaceDataForUserImage {
  workspaceUsers?: {
    userImages?: Record<string, string>;
    userMetadata?: Record<string, { image?: string }>;
  };
}

/** Workspace data shape for user avatar metadata. */
interface WorkspaceDataForAvatar {
  workspaceUsers?: {
    userImages?: Record<string, string>;
    userMetadata?: Record<string, {
      image?: string;
      isOwner?: boolean;
      isOnline?: boolean;
      userId?: string;
    }>;
    userData?: Record<string, { firstName?: string; lastName?: string }>;
  };
}

/** Return type for getUserAvatarData. */
export interface UserAvatarData {
  image: string | null;
  isOwner: boolean;
  isOnline: boolean;
  userId: string;
}

/**
 * Options for createWorkspaceUserLookup.
 * Use fallback to always get a displayable image URL (e.g. default avatar).
 */
export interface WorkspaceUserLookupOptions {
  /** URL to use when a user has no image (e.g. default avatar data URL). */
  fallback?: string;
}

/**
 * Pre-built lookup for display name, image, and avatar data by userId.
 * Use this instead of calling getUserDisplayName/getUserImage/getUserAvatarData
 * repeatedly so workspaceData is walked once per render.
 */
export interface WorkspaceUserLookup {
  getDisplayName: (userId: string) => string;
  getImage: (userId: string) => string | null;
  getAvatarData: (userId: string) => UserAvatarData;
}

// ============= Constants =============

/** Locale and options for date formatting in AskHub. */
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

// ============= Utilities =============

/**
 * Formats a date string to a readable locale string.
 *
 * @param dateString - ISO date string to format.
 * @returns Formatted date string (e.g. "Feb 2, 2025, 10:30 AM").
 * @throws Does not throw; invalid dates produce "Invalid Date" string.
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }
  return date.toLocaleDateString('en-US', DATE_FORMAT_OPTIONS);
};

/**
 * Gets a user's display name from workspace user data (firstName + lastName).
 *
 * @param userId - User ID to look up.
 * @param workspaceData - Workspace data containing user data.
 * @returns User's display name, or userId as fallback when not found.
 */
export const getUserDisplayName = (
  userId: string,
  workspaceData: WorkspaceDataForDisplayName | null
): string => {
  const data = workspaceData?.workspaceUsers?.userData?.[userId];
  return getUserDisplayNameFromRecord(data);
};

/**
 * Gets a user's image URL from workspace data.
 * Checks userImages first, then userMetadata.image.
 *
 * @param userId - User ID to look up.
 * @param workspaceData - Workspace data containing user images and metadata.
 * @param fallback - Optional URL to return when no image (e.g. DEFAULT_AVATAR_FALLBACK).
 * @returns User's image URL, fallback when provided and no image, or null.
 */
export const getUserImage = (
  userId: string,
  workspaceData: WorkspaceDataForUserImage | null,
  fallback?: string
): string | null => {
  if (!workspaceData?.workspaceUsers) {
    return fallback ?? null;
  }

  const { userImages, userMetadata } = workspaceData.workspaceUsers;

  if (userImages?.[userId]) {
    return userImages[userId];
  }

  if (userMetadata?.[userId]?.image) {
    return userMetadata[userId].image ?? fallback ?? null;
  }

  return fallback ?? null;
};

/**
 * Gets user metadata for avatar display (image, admin, online, userId).
 * Use optional fallback so image is never null when you need a displayable avatar.
 *
 * @param userId - User ID to look up.
 * @param workspaceData - Workspace data containing user metadata.
 * @param fallback - Optional URL when no image (e.g. DEFAULT_AVATAR_FALLBACK).
 * @returns Object with image (or fallback), isOwner, isOnline, and userId.
 */
export const getUserAvatarData = (
  userId: string,
  workspaceData: WorkspaceDataForAvatar | null,
  fallback?: string
): UserAvatarData => {
  const image = getUserImage(userId, workspaceData, fallback);
  const metadata = workspaceData?.workspaceUsers?.userMetadata?.[userId];

  return {
    image: image ?? null,
    isOwner: metadata?.isOwner ?? false,
    isOnline: metadata?.isOnline ?? false,
    userId: metadata?.userId ?? userId,
  };
};

/**
 * Builds a lookup from workspace data so you can resolve display name, image,
 * and avatar data by userId with O(1) lookups. Call once per workspaceData
 * (e.g. in useMemo) instead of calling getUserDisplayName/getUserImage/
 * getUserAvatarData for each user.
 *
 * @param workspaceData - Workspace data (or null).
 * @param options - Optional fallback URL for missing images.
 * @returns Lookup with getDisplayName, getImage, getAvatarData.
 */
export const createWorkspaceUserLookup =(
  workspaceData: WorkspaceDataForAvatar | null,
  options?: WorkspaceUserLookupOptions
): WorkspaceUserLookup => {
  const fallback = options?.fallback;
  const displayNames = new Map<string, string>();
  const images = new Map<string, string | null>();
  const avatarData = new Map<string, UserAvatarData>();

  if (workspaceData?.workspaceUsers) {
    const { userImages, userMetadata, userData } = workspaceData.workspaceUsers;
    const userIds = new Set<string>([
      ...Object.keys(userMetadata ?? {}),
      ...Object.keys(userImages ?? {}),
      ...Object.keys(userData ?? {}),
    ]);

    userIds.forEach((userId) => {
      const meta = userMetadata?.[userId];
      const data = userData?.[userId];
      const displayName = getUserDisplayNameFromRecord(data);
      displayNames.set(userId, displayName);

      const image =
        userImages?.[userId] ?? meta?.image ?? fallback ?? null;
      images.set(userId, image);

      avatarData.set(userId, {
        image: image ?? null,
        isOwner: meta?.isOwner ?? false,
        isOnline: meta?.isOnline ?? false,
        userId: meta?.userId ?? userId,
      });
    });
  }

  return {
    getDisplayName: (userId: string) =>
      displayNames.get(userId) ?? userId,
    getImage: (userId: string) =>
      images.get(userId) ?? fallback ?? null,
    getAvatarData: (userId: string) =>
      avatarData.get(userId) ?? {
        image: fallback ?? null,
        isOwner: false,
        isOnline: false,
        userId,
      },
  };
}
